import { supabase } from '../lib/supabase';

export const inviteService = {
  /**
   * Get all pending company invites for the current user
   * @returns {Promise<{invites: Array, count: number}>}
   */
  async getPendingInvites() {
    const { data, error } = await supabase.rpc('get_pending_invites');

    if (error) {
      console.error('Get pending invites error:', error);
      throw new Error('Failed to fetch pending invites');
    }

    if (!data?.success) {
      throw new Error(data?.error || 'Failed to fetch pending invites');
    }

    return {
      invites: data.invites || [],
      count: data.count || 0
    };
  },

  /**
   * Respond to a pending company invite
   * @param {string} inviteId - UUID of the invite
   * @param {boolean} accept - true to accept, false to reject
   * @returns {Promise<{success: boolean, action: 'accepted'|'rejected', companyId?: string, message: string}>}
   */
  async respondToInvite(inviteId, accept) {
    const { data, error } = await supabase.rpc('respond_to_invite', {
      p_invite_id: inviteId,
      p_accept: accept
    });

    if (error) {
      console.error('Respond to invite error:', error);
      throw new Error('Failed to respond to invite');
    }

    if (!data?.success) {
      throw new Error(data?.error || 'Failed to respond to invite');
    }

    return {
      success: true,
      action: data.action,
      companyId: data.company_id,
      companyAlias: data.company_alias,
      message: data.message
    };
  },

  /**
   * Get all companies the current user belongs to
   * @returns {Promise<{companies: Array, count: number}>}
   */
  async getUserCompanies() {
    // First try the RPC function
    const { data, error } = await supabase.rpc('get_user_companies');

    if (!error && data?.success) {
      return {
        companies: data.companies || [],
        count: data.count || 0
      };
    }

    // Fallback: Query company_users directly if RPC doesn't exist or fails
    console.log('RPC get_user_companies failed, using fallback query:', error?.message);

    const { data: fallbackData, error: fallbackError } = await supabase
      .from('company_users')
      .select(`
        company_id,
        role,
        company_alias,
        is_active,
        created_at,
        companies (
          id,
          company_name,
          company_tag,
          status
        )
      `)
      .eq('is_active', true);

    if (fallbackError) {
      console.error('Fallback query error:', fallbackError);
      throw new Error('Failed to fetch user companies');
    }

    // Transform to expected format
    const companies = (fallbackData || [])
      .filter(cu => cu.companies?.status === 'active')
      .map(cu => ({
        company_id: cu.company_id,
        company_name: cu.companies?.company_name,
        company_tag: cu.companies?.company_tag,
        role: cu.role,
        company_alias: cu.company_alias,
        is_active: cu.is_active,
        joined_at: cu.created_at
      }));

    return {
      companies,
      count: companies.length
    };
  }
};
